// services/saleng.js
import axios, { getAuthorizationHeaders } from "./apiClient";
import { jwtDecode } from "jwt-decode";
import { getSaleOrderPricing, getLockSpecialPrice } from "../utils/lockPricing";
import { SALENG_GREEN_API_PROD, SALENG_SHOP_API_PROD } from "./../config";

const SALENG_GREEN_API = SALENG_GREEN_API_PROD;
const SALENG_SHOP_API = SALENG_SHOP_API_PROD;

const getAccessToken = () => {
  const token = localStorage.getItem("accessToken");
  if (!token) return null;

  try {
    return JSON.parse(token);
  } catch {
    return token;
  }
};

const decodeToken = () => {
  const token = getAccessToken();
  if (!token) return null;
  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
};

const getIds = () => {
  const decoded = decodeToken();
  return {
    accessToken: getAccessToken(),
    userId: decoded?.id ?? null,
    username: decoded?.username ?? null,
    shopId: decoded?.shopId ?? null,
    branchId: Number(localStorage.getItem("branchId")),
    isMain: decoded?.isMain ?? false,
  };
};

export const getSalengContext = () => {
  const { shopId, branchId } = getIds();
  return { shopId, branchId };
};

/* ---------------- Auth ---------------- */
export const loginShop = (payload) =>
  axios.post(`${SALENG_SHOP_API}/auth/login`, payload, {
    headers: { "Content-Type": "application/json" },
  });

export const getAuthUser = () =>
  axios.get(`${SALENG_SHOP_API}/auth`, {
    headers: { "Content-Type": "application/json" },
  });

/* ---------------- Categories ---------------- */
export const getCategories = () => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.get(`${SALENG_SHOP_API}/categories/${shopId}`, {
    headers: { "Content-Type": "application/json" },
  });
};

/* ---------------- Products ---------------- */
export const getProductsBranch = (visible, transactionId) => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/products/data`, {
    params: { shopId, branchId, transactionId, visible },
    headers: { "Content-Type": "application/json" },
  });
};

export const getProducts = () => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.get(`${SALENG_SHOP_API}/products/${shopId}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const getProductGroupsAdjustments = async (customerGroupId) => {
  const res = await axios.get(
    `${SALENG_SHOP_API}/products/adjustments/groups/${customerGroupId}`,
    {
      headers: { "Content-Type": "application/json" },
    }
  );

  return res.data?.data || res.data || [];
};

export const createProduct = (payload) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/products`,
    { ...payload, shopId },
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

export const deleteProduct = (id) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.delete(`${SALENG_SHOP_API}/products/${shopId}/${id}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const updateProduct = (id, payload) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.put(`${SALENG_SHOP_API}/products/${shopId}/${id}`, payload, {
    headers: { "Content-Type": "application/json" },
  });
};

export const getProductPrice = ({ productId, customerId }) => {
  console.log("productId", productId);
  console.log("customerId", customerId);

  const { branchId, shopId } = getIds();
  if (!branchId) return Promise.reject("Missing branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/products/price`, {
    params: { productId, branchId, shopId, customerId },
    headers: { "Content-Type": "application/json" },
  });
};

/* ---------------- Shop ---------------- */
export const getShop = (id) => {
  const { shopId } = getIds();
  const targetId = id ?? shopId;
  if (!targetId) return Promise.reject("Missing shopId (token?)");
  return axios.get(`${SALENG_SHOP_API}/shop/${targetId}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const createShop = (payload, _shopId) => {
  const { shopId } = getIds();
  const finalShopId = _shopId ?? shopId;
  if (!finalShopId) return Promise.reject("Missing shopId (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/shop`,
    { ...payload, shopId: finalShopId },
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

/* ---------------- Branches ---------------- */
export const createBranch = (payload) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/branches`,
    { ...payload, shopId },
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

export const deleteBranch = (id) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.delete(`${SALENG_SHOP_API}/branches/${shopId}/${id}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const updateBranch = (id, payload) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.put(`${SALENG_SHOP_API}/branches/${shopId}/${id}`, payload, {
    headers: { "Content-Type": "application/json" },
  });
};

/* ---------------- Transactions (Purchase) ---------------- */
export const getPurchaseTransactions = (params = {}) => {
  const { shopId, branchId } = getIds();
  return axios.get(`${SALENG_SHOP_API}/transaction/purchases-transaction`, {
    params: {
      shopId: params.shopId ?? shopId,
      branchId: params.branchId ?? branchId,
      ...params,
    },
    headers: { "Content-Type": "application/json" },
  });
};

export const getTransaction = (transactionId) =>
  axios.get(`${SALENG_SHOP_API}/transaction/${transactionId}`, {
    headers: { "Content-Type": "application/json" },
  });

export const getTransactionByStatus = async (status, startDate, endDate) => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId) throw new Error("Missing shopId/branchId (token?)");

  try {
    const res = await axios.get(`${SALENG_SHOP_API}/transaction/status`, {
      params: { status, shopId, branchId, startDate, endDate },
      headers: { "Content-Type": "application/json" },
    });

    return res.data?.data || res.data || [];
  } catch (err) {
    console.error("Error fetching transactions:", err);
    return [];
  }
};

export const getBranchTransaction = () => {
  const { branchId } = getIds();
  if (!branchId) return Promise.reject("Missing branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/transaction/branch/${branchId}`, {
    headers: { "Content-Type": "application/json" },
  });
};

const generateBillNumber = (branchCodeOrId, lastNumber) => {
  const today = new Date();
  const datePart = today.toISOString().slice(0, 10).replace(/-/g, "");
  const newNumber = String(lastNumber + 1).padStart(3, "0");
  const formattedBranch = String(branchCodeOrId).padStart(2, "0");
  return `B${formattedBranch}-${datePart}-${newNumber}`;
};

export const createTransaction = async (payload) => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");

  return axios.post(
    `${SALENG_SHOP_API}/transaction`,
    { ...payload, branchId, shopId },
    { headers: { "Content-Type": "application/json" } }
  );
};

export const updateTransaction = (payload, id) => {
  const { username } = getIds();
  const body = { ...payload };

  if (body.hasWeighed) body.weighedByNames = username;
  if (body.hasPaid) body.paidByNames = username;

  delete body.hasWeighed;
  delete body.hasPaid;

  return axios.put(`${SALENG_SHOP_API}/transaction/${id}`, body, {
    headers: { "Content-Type": "application/json" },
  });
};

export const deleteItemTransaction = (transactionId, id) =>
  axios.delete(`${SALENG_SHOP_API}/transaction/item/${transactionId}/${id}`, {
    headers: { "Content-Type": "application/json" },
  });

/* ---------------- Transactions (Sale) ---------------- */
export const getSaleTransactions = (params = {}) => {
  const { shopId, branchId } = getIds();
  return axios.get(`${SALENG_SHOP_API}/sale/sale-transaction`, {
    params: {
      shopId: params.shopId ?? shopId,
      branchId: params.branchId ?? branchId,
      ...params,
    },
    headers: { "Content-Type": "application/json" },
  });
};

export const getSaleTransaction = (transactionId) =>
  axios.get(`${SALENG_SHOP_API}/sale/${transactionId}`, {
    headers: { "Content-Type": "application/json" },
  });

export const getBranchSaleTransaction = () => {
  const { branchId } = getIds();

  if (!branchId) return Promise.reject("Missing branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/sale/branch/${branchId}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const createSaleTransaction = async (payload) => {
  try {
    const { shopId, branchId, username } = getIds();
    if (!shopId || !branchId) {
      return Promise.reject("Missing shopId/branchId (token?)");
    }
    const transactionsResponse = await getBranchSaleTransaction();
    const transactions = transactionsResponse?.data?.data ?? [];
    const billNumber = generateBillNumber(branchId, transactions.length);

    const body = {
      ...payload,
      openByNames: username,
      shopId,
      branchId,
      billNumber,
    };
    return await axios.post(
      `${SALENG_SHOP_API}/sale`, body,
      { headers: { "Content-Type": "application/json" } }
    );

  } catch (err) {
    console.error("createSaleTransaction error:", err);
    return Promise.reject(err);
  }
};
// export const createSaleTransaction = async (payload) => {
//   const { shopId, branchId } = getIds();
//   if (!shopId || !branchId)
//     return Promise.reject("Missing shopId/branchId (token?)");

//   const transactionsResponse = await getBranchSaleTransaction();
//   const transactions = transactionsResponse?.data?.data ?? [];
//   const billNumber = generateBillNumber(branchId, transactions.length);

//   return axios.post(
//     `${SALENG_SHOP_API}/sale`,
//     { ...payload, branchId, shopId, billNumber },
//     { headers: { "Content-Type": "application/json" } }
//   );
// };

export const updateSaleTransaction = (payload, id) =>
  axios.put(`${SALENG_SHOP_API}/sale/${id}`, payload, {
    headers: { "Content-Type": "application/json" },
  });

export const deleteItemSaleTransaction = (transactionId, id) =>
  axios.delete(`${SALENG_SHOP_API}/sale/item/${transactionId}/${id}`, {
    headers: { "Content-Type": "application/json" },
  });

export const getSaleTransactionByStatus = (status, startDate, endDate) => {
  const { shopId, branchId } = getIds();

  return axios.get(`${SALENG_SHOP_API}/sale/status`, {
    params: { status, shopId, branchId, startDate, endDate },
    headers: { "Content-Type": "application/json" },
  });
};

/* ---------------- Stock ---------------- */
export const updateStock = (payload) => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/stock/inventory/receive/${shopId}/${branchId}`,
    payload,
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};
export const cancelStock = (payload) => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/stock/inventory/cancel-receive/by-transaction/${shopId}/${branchId}`,
    payload,
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};
export const updateStockSell = (payload) => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/stock/inventory/sell/${shopId}/${branchId}`,
    payload,
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

/* ---------------- Inventory ---------------- */
export const getInventoryTransaction = (params = {}) => {
  const { shopId, branchId } = getIds();
  return axios.get(`${SALENG_SHOP_API}/stock/inventory-transaction`, {
    params: {
      shopId: params.shopId ?? shopId,
      branchId: params.branchId ?? branchId,
      ...params,
    },
    headers: { "Content-Type": "application/json" },
  });
};

export const getInventory = () => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/stock/inventory`, {
    params: { shopId, branchId },
    headers: { "Content-Type": "application/json" },
  });
};

export const getInventoryById = (id) => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/stock/inventory/${id}`, {
    params: { shopId, branchId },
    headers: { "Content-Type": "application/json" },
  });
};

/* ---------------- Green API (users/points) ---------------- */
export const getUserByPhone = (phoneNumber) =>
  axios.get(`${SALENG_GREEN_API}/users/by-phone/${phoneNumber}`, {
    headers: { "Content-Type": "application/json" },
  });

export const addPointToUser = (id, body) =>
  axios.post(`${SALENG_GREEN_API}/users/${id}/add-point`, body, {
    headers: { "Content-Type": "application/json" },
  });

/* ---------------- Files ---------------- */
export const Upload = (formData) =>
  fetch(`${SALENG_SHOP_API}/files`, {
    method: "POST",
    headers: getAuthorizationHeaders(),
    body: formData,
  });

/* ---------------- Purchase Orders ---------------- */
export const getPurchaseOrders = async (params) => {
  const { branchId } = getIds();

  const res = await axios.get(`${SALENG_SHOP_API}/purchase-orders`, {
    params: { ...params, branchId },
    headers: { "Content-Type": "application/json" },
  });

  return res.data?.data || res.data || [];
};

export const createPurchaseOrders = (payload) => {
  const { branchId, userId } = getIds();

 const res = axios.post(
    `${SALENG_SHOP_API}/purchase-orders`,
    { ...payload, branchId, userId },
    {
      headers: { "Content-Type": "application/json" },
    }
  );
    return res
};

export const updatePurchaseOrders = (id, payload) => {
  const { branchId } = getIds();

  const res = axios.put(
    `${SALENG_SHOP_API}/purchase-orders/${id}`,
    { ...payload, branchId },
    {
      headers: { "Content-Type": "application/json" },
    }
  );
  return res
};

export const updatePurchaseOrderStatus = async (id, payload) => {
  const { branchId } = getIds();

  const res = await axios.put(
    `${SALENG_SHOP_API}/purchase-orders/${id}/status`,
    { ...payload, branchId },
    {
      headers: { "Content-Type": "application/json" },
    }
  );

  return res.data?.data || res.data || [];
};

/* ---------------- Sale Orders ---------------- */
export const getSaleOrders = (params = {}) => {
  const { shopId, branchId } = getIds();
  return axios.get(`${SALENG_SHOP_API}/sale-orders`, {
    params: { ...params, shopId, branchId },
    headers: { "Content-Type": "application/json" },
  });
};

export const createSaleOrders = (payload) => {
  const { branchId } = getIds();
  return axios.post(`${SALENG_SHOP_API}/sale-orders`, { ...payload, branchId }, {
    headers: { "Content-Type": "application/json" },
  });
};

export const updateSaleOrders = (id, payload) => {
  const { branchId } = getIds();
  return axios.put(`${SALENG_SHOP_API}/sale-orders/${id}`, { ...payload, branchId }, {
    headers: { "Content-Type": "application/json" },
  });
};

/* ---------------- Transform ---------------- */
export const getTransformTransaction = () => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/transform`, {
    params: { shopId, branchId },
    headers: { "Content-Type": "application/json" },
  });
};

export const getTransformTransactionById = (id) =>
  axios.get(`${SALENG_SHOP_API}/transform/${id}`, {
    headers: { "Content-Type": "application/json" },
  });

export const createTransformTransaction = (payload) => {
  const { shopId, branchId, userId } = getIds();
  if (!shopId || !branchId || !userId)
    return Promise.reject("Missing ids (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/transform`,
    { ...payload, transformBy: userId, shopId, branchId },
    { headers: { "Content-Type": "application/json" } }
  );
};

export const getCustomers = () => {
  const { shopId, branchId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  return axios.get(`${SALENG_SHOP_API}/customer`, {
    params: { shopId, branchId },
    headers: { "Content-Type": "application/json" },
  });
};

/* ---------------- Pakeage ---------------- */

export async function getPackages() {
  const { shopId, branchId } = getIds();

  const { data } = await axios.get(`${SALENG_SHOP_API}/stock/package`, {
    params: { shopId, branchId },
    headers: { "Content-Type": "application/json" },
  });

  return data?.data ?? data;
}

export async function fetchPackagesByProduct(productId) {
  const { shopId, branchId } = getIds();

  const { data } = await axios.get(`${SALENG_SHOP_API}/stock/package`, {
    params: { productId, shopId, branchId },
    headers: { "Content-Type": "application/json" },
  });

  return data?.data ?? data;
}

export async function upsertPackage(payload) {
  const { shopId, branchId } = getIds();

  const { data } = await axios.post(
    `${SALENG_SHOP_API}/stock/package`,
    { ...payload, shopId, branchId },
    { headers: { "Content-Type": "application/json" } }
  );
  return data;
}

export async function deletePackage(id) {
  const { data } = await axios.delete(
    `${SALENG_SHOP_API}/stock/package/${id}`,
    {
      headers: { "Content-Type": "application/json" },
    }
  );
  return data;
}

export async function adjustPurchaseOrderItemWeight(id, delta) {
  const res = await fetch(
    `${SALENG_SHOP_API}/purchase-orders/items/${id}/actual-weight`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...getAuthorizationHeaders(),
      },
      body: JSON.stringify({ delta }),
    }
  );
  if (!res.ok)
    throw new Error(`adjustPurchaseOrderItemWeight failed: ${res.status}`);
  return res.json().catch(() => ({}));
}

/* ---------------- LockPricing ---------------- */

export const getLockSellProducts = () => {
  const { shopId, branchId } = getIds();
  return axios.get(`${SALENG_SHOP_API}/lock-pricing/sell-products`, {
    params: { shopId, actorBranchId: branchId },
  });
};

export const convertLockSellProduct = (id, payload) => {
  const { shopId, branchId } = getIds();
  return axios.post(`${SALENG_SHOP_API}/lock-pricing/${id}/sell-product`, {
    ...payload, shopId, actorBranchId: branchId,
  });
};

const withLockSpecialPrices = async (response) => {
  if (!Array.isArray(response?.data) || response.data.length === 0) return response;
  const shopId = response.data[0].shopId ?? getIds().shopId;
  const result = await getSaleOrders({ shopId });
  const saleOrders = result?.data?.data ?? result?.data ?? [];
  const { prices } = getSaleOrderPricing(saleOrders, shopId);
  return {
    ...response,
    data: response.data.map((lock) => ({
      ...lock,
      specialPrice: getLockSpecialPrice(lock, prices),
    })),
  };
};

export async function getLockPricingByCustomer(customerId, masterId, includeClosed = false) {
  const { data } = await axios.get(`${SALENG_SHOP_API}/lock-pricing`, {
    params: {
      customerId,
      ...(masterId ? { masterId } : {}),
      ...(includeClosed ? { includeClosed: true } : {}),
    },
  });
  return withLockSpecialPrices(data);
}

export async function getLockPricingByBranch(branchId, shopId, includeClosed = false) {
  const { shopId: tokenShopId, branchId: tokenBranchId } = getIds();
  const targetBranchId = branchId ?? tokenBranchId;
  const targetShopId = shopId ?? tokenShopId;
  const { data } = await axios.get(`${SALENG_SHOP_API}/lock-pricing`, {
    params: {
      branchId: targetBranchId,
      shopId: targetShopId,
      ...(includeClosed ? { includeClosed: true } : {}),
    },
  });
  return withLockSpecialPrices(data);
}

export async function getSaleLockPricingOptions(branchId, shopId, transactionId) {
  const { data } = await axios.get(`${SALENG_SHOP_API}/lock-pricing/sale-options`, {
    params: { branchId, shopId, transactionId },
  });
  return data;
}

export async function getLockPricingMastersByCustomer(customerId) {
  const { data } = await axios.get(`${SALENG_SHOP_API}/lock-pricing/masters`, {
    params: { customerId },
  });
  return data;
}

export async function getLockPricingMastersByBranch(branchId, shopId) {
  const { shopId: tokenShopId, branchId: tokenBranchId } = getIds();
  const { data } = await axios.get(`${SALENG_SHOP_API}/lock-pricing/masters`, {
    params: {
      branchId: branchId ?? tokenBranchId,
      shopId: shopId ?? tokenShopId,
    },
  });
  return data;
}

export const updateLockPricingMasterStatus = (masterId, status) =>
  axios.patch(`${SALENG_SHOP_API}/lock-pricing/masters/${masterId}/status`, {
    status,
  });

export const closeLockPricing = (lockPricingId) =>
  axios.patch(`${SALENG_SHOP_API}/lock-pricing/${lockPricingId}/close`);

export const updateLockPricingContractProfit = (
  lockPricingId,
  { seeContractProfit, salePrice, incentive },
) =>
  axios.patch(`${SALENG_SHOP_API}/lock-pricing/${lockPricingId}`, {
    seeContractProfit,
    salePrice,
    incentive,
  });

export const saveDailyPriceSnapshot = (payload) =>
  axios.post(`${SALENG_SHOP_API}/lock-pricing/daily`, payload, {
    headers: { "Content-Type": "application/json" },
  });

export const consumeLockPricing = (payload) =>
  axios.post(`${SALENG_SHOP_API}/lock-pricing/consume`, payload, {
    headers: { "Content-Type": "application/json" },
  });
/* ---------------- LockPricing ---------------- */
export const saveLayout = (payload) => {
  const { shopId, branchId, userId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  const body = {
    ...payload,
    userId,
  };
  return axios.post(`${SALENG_SHOP_API}/products/${shopId}/layout`, body, {
    headers: { "Content-Type": "application/json" },
  });
};

export const getUserLayout = async () => {
  const { shopId, branchId, userId } = getIds();
  if (!shopId || !branchId)
    return Promise.reject("Missing shopId/branchId (token?)");
  const res = await axios.get(`${SALENG_SHOP_API}/products/${shopId}/layout`, {
    params: { userId },
  });
  return res.data;
};

/* ---------------- Users ---------------- */
export const getUser = (id) => {
  const { shopId } = getIds();

  axios.get(`${SALENG_SHOP_API}/users/${id}`, {
    params: { shopId },
    headers: { "Content-Type": "application/json" },
  });
};

export const createUser = (payload) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.post(
    `${SALENG_SHOP_API}/users`,
    { ...payload, shopId },
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};

export const deleteUser = (id) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.delete(`${SALENG_SHOP_API}/users/${shopId}/${id}`, {
    headers: { "Content-Type": "application/json" },
  });
};

export const updateUser = (id, payload) => {
  const { shopId } = getIds();
  if (!shopId) return Promise.reject("Missing shopId (token?)");
  return axios.put(`${SALENG_SHOP_API}/users/${shopId}/${id}`, payload, {
    headers: { "Content-Type": "application/json" },
  });
};

export const getBestPrice = async (transactionId) => {
  const res = await axios.get(
    `${SALENG_SHOP_API}/transaction/price/best-history`,
    {
      params: { transactionId },
      headers: { "Content-Type": "application/json" },
    }
  );
  return res.data?.data || res.data || [];
};

export const getLessReport = (payload) => {
  return axios.post(
    `${SALENG_SHOP_API}/report/generate-excel`,payload,
    {
      headers: { "Content-Type": "application/json" },
    }
  );
};
